import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { Info, AlertTriangle, RotateCcw } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/workspace-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';
import type { BinStockDto } from '@features/inventory';
import type { CreatePicklistBinRequest } from '../types';
import { binIdentity, lineTag, plannedLabel, type ItemPlan, type SavedAllocation, rowsOverTheirBin } from './itemPlan';
import { ReceivingItemType } from '../types';
import { commitmentCaption, commitmentLabel, commitmentOf, serialIdsOf, splitByCommitment } from '../utils/serialCommitments';
import { binHoldCaption, splitByBinHolds } from '../utils/freeStock';
import { useSerialCommitments } from './useSerialCommitments';
import { useBinHolds } from './useBinHolds';

interface BinAllocationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * The whole plan for ONE ITEM across the picklist — not one MRS line's share (14ykpqtnxbv §4).
   * Opening per line is what let two lines of one item be allocated the same serials.
   */
  plan: ItemPlan;
  availableBins: BinStockDto[];
  /**
   * Serial-level bins are fetched lazily when this dialog opens, so it renders before its rows
   * exist. While true, show a skeleton — never an empty table, which reads as "no stock" and is the
   * same confusion this whole change set exists to remove.
   */
  isLoadingBins?: boolean;
  /** Non-null when the bin fetch failed. Shown inline with a retry, never swallowed. */
  binsError?: string | null;
  onRetryBins?: () => void;
  /** Add Items only: the picklist being added to, whose own serials are not "another picklist's". */
  excludePicklistId?: number;
  /** The user's saved allocation: which bins and serials go out for the item, and how many from each. */
  currentOverride: SavedAllocation | null;
  onSave: (saved: SavedAllocation) => void;
  onReset: () => void;
}

/**
 * A row other picklists hold: shown, never chosen — a held serial (owner, 2026-10-05) or a lot / untracked bin held
 * entirely (14ykpqtv7nd). Native `title`, not a Radix tooltip: the row repeats per serial or bin (UI-GUIDELINES §8).
 */
function HeldRow({
  rowKey,
  committed,
  bin,
  gridCols,
  text,
  tone,
  title,
}: {
  rowKey: string;
  committed: string;
  bin: BinStockDto;
  gridCols: string;
  text: string;
  tone: string;
  title: string;
}) {
  return (
    <div
      data-row-key={rowKey}
      data-committed={committed}
      aria-disabled="true"
      className="grid items-center gap-2.5 border-b border-border bg-muted/30 px-3 py-[7px]"
      style={{ gridTemplateColumns: gridCols }}
    >
      <span className="font-mono text-xs font-medium text-muted-foreground">{bin.binCode}</span>
      <span className="font-mono text-[11.5px] text-muted-foreground">{bin.inventoryNumber ?? '—'}</span>
      <span className="col-span-2 flex min-w-0 justify-end">
        <span
          title={title}
          className={cn(
            'inline-flex h-5 min-w-0 cursor-help items-center rounded-full px-2 text-[10.5px] font-semibold',
            tone,
          )}
        >
          <span className="truncate">{text}</span>
        </span>
      </span>
    </div>
  );
}

/** Composite key for bin + inventory number, so a serial's rows in one bin stay separate. */
function binKey(binId: number, inventoryNumberId: number | null): string {
  return binIdentity({ binId, inventoryNumberId });
}

function binKeyFromStock(bin: BinStockDto): string {
  return binKey(bin.binId, bin.inventoryNumberId);
}

/** A saved allocation, back in the per-row shape the inputs use. */
function overrideToAllocations(bins: CreatePicklistBinRequest[]): Record<string, number> {
  const map: Record<string, number> = {};
  for (const bin of bins) map[binKey(bin.binId, bin.inventoryNumberId)] = bin.allocatedQuantity;
  return map;
}

/**
 * Client-side best-fit bin allocation matching the server algorithm.
 * Uses composite key (binId + inventoryNumberId) so serialized items
 * with the same bin but different serials are treated as separate entries.
 */
function autoAllocateBins(
  quantityNeeded: number,
  bins: BinStockDto[],
): Record<string, number> {
  const candidates = bins.filter((b) => b.quantityAvailable > 0);
  if (candidates.length === 0) return {};

  // Best-fit: smallest bin that fully satisfies
  const bestFit = candidates
    .filter((b) => b.quantityAvailable >= quantityNeeded)
    .sort((a, b) => a.quantityAvailable - b.quantityAvailable)[0];

  if (bestFit) {
    return { [binKeyFromStock(bestFit)]: quantityNeeded };
  }

  // Multi-bin: largest-first
  const sorted = [...candidates].sort(
    (a, b) => b.quantityAvailable - a.quantityAvailable,
  );
  const result: Record<string, number> = {};
  let remaining = quantityNeeded;

  for (const bin of sorted) {
    if (remaining <= 0) break;
    const allocateQty = Math.min(remaining, bin.quantityAvailable);
    result[binKeyFromStock(bin)] = allocateQty;
    remaining -= allocateQty;
  }

  return result;
}

/**
 * Allocate Bins — which bins and serials go out for ONE ITEM, and how many from each.
 *
 * It no longer says which MRS line each bin serves (PT-7 F1, the derived model): the picker chooses the MRS
 * line when they pick, and the server rebalances. The cart still splits the saved bins across the item's
 * lines before it sends them (`splitBinsForPayload`), but that split is provisional and no screen draws it.
 * The numbered legend and the footer's split line stay, read from the PLANNED quantities.
 *
 * **One serial, one open picklist at a time (owner, 2026-10-05).** NetSuite counts a serial on hand until its MIS
 * posts, so the stock read lists serials another open picklist already holds. For a serialized item the dialog asks
 * the server which those are, offers only the free ones, and shows the rest — labelled with the picklist that holds
 * them, never selectable — behind "Show all serials". The server refuses a held serial on submit either way.
 */
export function BinAllocationDialog({
  open,
  onOpenChange,
  plan,
  availableBins,
  isLoadingBins = false,
  binsError = null,
  onRetryBins,
  excludePicklistId,
  currentOverride,
  onSave,
  onReset,
}: BinAllocationDialogProps) {
  // The item's identity and unit are the same on every line; the quantity is the item's whole plan.
  const item = plan.lines[0].item;
  const pickQuantity = plan.total;
  const multiLine = plan.lines.length > 1;
  // 86d3gymhz — lot items are BIN-ONLY at pick (the exact lot is resolved late at post by FEFO), so
  // collapse the per-lot stock rows into one row per bin (null inventory number). Serial keeps its
  // per-unit identity; inventory has no lot rows anyway. Everything below (auto-allocate, table, save)
  // then operates bin-only for lots with no further branching.
  const effectiveBins = useMemo<BinStockDto[]>(() => {
    if (item.itemType !== ReceivingItemType.LotNumbered) return availableBins;
    const byBin = new Map<number, BinStockDto>();
    for (const b of availableBins) {
      const existing = byBin.get(b.binId);
      if (existing) {
        existing.quantityAvailable += b.quantityAvailable;
      } else {
        byBin.set(b.binId, { ...b, inventoryNumberId: null, inventoryNumber: null });
      }
    }
    return Array.from(byBin.values());
  }, [availableBins, item.itemType]);

  // Local allocation state keyed by composite "binId:inventoryNumberId" — default to auto-allocate
  const [allocations, setAllocations] = useState<Record<string, number>>(() => {
    if (currentOverride) return overrideToAllocations(currentOverride.bins);
    return autoAllocateBins(pickQuantity, effectiveBins.filter((b) => b.quantityAvailable > 0));
  });

  // Reset local state when dialog opens with new data
  const handleOpenChange = useCallback(
    (isOpen: boolean) => {
      if (isOpen && currentOverride) {
        setAllocations(overrideToAllocations(currentOverride.bins));
      } else if (isOpen) {
        // Default to auto-allocate
        const candidates = effectiveBins.filter((b) => b.quantityAvailable > 0);
        setAllocations(autoAllocateBins(pickQuantity, candidates));
      }
      onOpenChange(isOpen);
    },
    [currentOverride, onOpenChange, effectiveBins, pickQuantity],
  );

  // Serials other open picklists hold. Asked once the stock rows are in hand, and only for a serialized item: a
  // lot's inventory number is not a unit, and many picklists rightly share one lot.
  const isSerialized = item.itemType === ReceivingItemType.Serialized;
  const serialIds = useMemo(() => (isSerialized ? serialIdsOf(effectiveBins) : []), [isSerialized, effectiveBins]);
  const commitments = useSerialCommitments(open && !isLoadingBins, serialIds, excludePicklistId);
  const heldBySerial = commitments.bySerial;
  // A lot or untracked item has no serial to name, so what other picklists hold comes off its bins as a QUANTITY
  // (14ykpqtv7nd) — the same per-bin figure creation checks a hand-picked claim against.
  const binHolds = useBinHolds(open && !isLoadingBins && !isSerialized, plan.netsuiteItemId, excludePicklistId);
  // The skeleton covers EVERY read: drawing the rows before one lands would offer held stock for a moment.
  const isLoadingRows = isLoadingBins || commitments.isLoading || binHolds.isLoading;
  const [showAll, setShowAll] = useState(false);

  // `binsWithStock` is what may be ALLOCATED (free rows, at their free quantity); `committedBins` is shown, never
  // chosen. Serials split by name; lot and untracked bins by the quantity other picklists hold there.
  const holdSplit = useMemo(
    () => (isSerialized ? null : splitByBinHolds(effectiveBins, binHolds.byBin)),
    [isSerialized, effectiveBins, binHolds.byBin],
  );
  const { free: binsWithStock, committed: committedBins } = useMemo(
    () => (holdSplit ? { free: holdSplit.free, committed: holdSplit.held } : splitByCommitment(effectiveBins, heldBySerial)),
    [holdSplit, effectiveBins, heldBySerial],
  );
  /** Rows of a lot or untracked item that other picklists hold entirely — drawn greyed, never chosen. */
  const heldBinKeys = useMemo(
    () => new Set(holdSplit ? holdSplit.held.map(binKeyFromStock) : []),
    [holdSplit],
  );
  const visibleBins = useMemo(
    () =>
      !showAll
        ? binsWithStock
        : holdSplit
          ? [...binsWithStock, ...committedBins]
          : effectiveBins.filter((b) => b.quantityAvailable > 0),
    [showAll, holdSplit, effectiveBins, binsWithStock, committedBins],
  );

  /**
   * Auto-allocate once the lazily-fetched bins actually arrive.
   *
   * The `useState` initialiser above runs at mount, which is now BEFORE the serial-level bins exist
   * — it would auto-allocate against an empty list and leave the dialog showing nothing allocated
   * even though bins were available. The ref makes this fire once per open, so it seeds the default
   * without stamping over edits the user has already made.
   */
  const hasSeededAllocations = useRef(false);

  useEffect(() => {
    if (!open) {
      hasSeededAllocations.current = false;
      return;
    }
    if (hasSeededAllocations.current) return;
    // An override is the user's own saved choice and wins over any default.
    if (currentOverride) {
      hasSeededAllocations.current = true;
      return;
    }
    if (isLoadingRows || binsWithStock.length === 0) return;

    setAllocations(autoAllocateBins(pickQuantity, binsWithStock));
    hasSeededAllocations.current = true;
  }, [open, currentOverride, isLoadingRows, binsWithStock, pickQuantity]);

  // Counted over the rows that can be saved, which is also what `handleSave` writes. A saved choice whose serial
  // another picklist has since taken drops out of the total, so the dialog reads "1 remaining" and asks for another.
  const totalAllocated = useMemo(
    () => binsWithStock.reduce((sum, bin) => sum + (allocations[binKeyFromStock(bin)] ?? 0), 0),
    [allocations, binsWithStock],
  );
  const lostChoices = useMemo(
    () => committedBins.filter((bin) => (allocations[binKeyFromStock(bin)] ?? 0) > 0).length,
    [allocations, committedBins],
  );

  // ISS-R-038 — a row may not ask its bin for more than the bin has. The row used to be marked only: with the
  // total matching, Save stayed enabled and the server refused it later, when the picklist was created.
  const overRows = useMemo(
    () => rowsOverTheirBin(binsWithStock.map((bin) => ({
      allocated: allocations[binKeyFromStock(bin)] ?? 0,
      available: bin.quantityAvailable,
    }))),
    [allocations, binsWithStock],
  );
  const isValid = totalAllocated === pickQuantity && overRows === 0;
  const remaining = pickQuantity - totalAllocated;

  const handleAllocationChange = useCallback(
    (key: string, value: number) => {
      setAllocations((prev) => {
        if (value <= 0) {
          const rest = { ...prev };
          delete rest[key];
          return rest;
        }
        return { ...prev, [key]: value };
      });
    },
    [],
  );

  const handleAutoAllocate = useCallback(() => {
    setAllocations(autoAllocateBins(pickQuantity, binsWithStock));
  }, [pickQuantity, binsWithStock]);

  const handleResetToAuto = useCallback(() => {
    onReset();
    onOpenChange(false);
  }, [onReset, onOpenChange]);

  const handleSave = useCallback(() => {
    // The Save button is disabled off `isValid`, but this handler is what writes the record's input and
    // should be able to refuse on its own. A partial allocation reaching `binOverrides` outlives the
    // dialog: the submit path splits the saved bins across the lines and re-checks only the total.
    if (totalAllocated !== pickQuantity || overRows > 0) return;

    const entries: CreatePicklistBinRequest[] = binsWithStock
      .filter((bin) => (allocations[binKeyFromStock(bin)] ?? 0) > 0)
      .map((bin) => ({
        binId: bin.binId,
        binCode: bin.binCode,
        locationId: bin.locationId,
        inventoryNumberId: bin.inventoryNumberId,
        inventoryNumber: bin.inventoryNumber,
        allocatedQuantity: allocations[binKeyFromStock(bin)],
      }));
    onSave({ bins: entries });
    onOpenChange(false);
  }, [allocations, binsWithStock, onSave, onOpenChange, totalAllocated, pickQuantity, overRows]);

  // The prototype's `cr.bin.grid` (O-20 settlement): Bin | Lot / Serial | Avail | Allocate. Lot / Serial is
  // NOT conditional — the prototype renders it for every item, with a dash where there is no number.
  const gridCols = 'minmax(120px,1fr) minmax(120px,1fr) 96px 120px';

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      {/* The prototype's shell is `width:100%` in a 24px-padded scrim (O-20 settlement: the old `max-w-5xl`
          cap goes with the Serves column it was sized around). */}
      <DialogContent
        overlayClassName="bg-black/50"
        className="flex max-h-[calc(100%-3rem)] w-[calc(100%-3rem)] max-w-none flex-col gap-0 p-0"
      >
        <DialogHeader className="flex-none space-y-0 px-5 pb-[14px] pt-5 text-left">
          <DialogTitle className="text-base font-semibold">Allocate Bins</DialogTitle>
          {/* 86d48d39v: ItemName is uncapped in the DB - clamp the subtitle */}
          <DialogDescription className="!mt-[5px] line-clamp-2 text-[12.5px]">
            <span className="font-mono">{item.itemCode}</span> — {item.itemName} — Plan:{' '}
            <b className="font-semibold text-foreground">
              {pickQuantity} {item.unitOfMeasure}
              {multiLine ? ` across ${plan.lines.length} MRS lines` : ''}
            </b>
          </DialogDescription>
        </DialogHeader>

        {multiLine && (
          <>
            <div className="mx-5 mb-2.5 flex flex-shrink-0 items-start gap-2 rounded-lg border border-info/30 bg-info/[0.08] px-[11px] py-[9px]">
              <Info className="mt-px h-3.5 w-3.5 shrink-0 text-info" />
              <span className="text-[11.5px] leading-[1.5]">
                The whole plan for this item on this picklist — all {pickQuantity}{' '}
                {item.unitOfMeasure}, across every MRS line that asked for it.
              </span>
            </div>

            {/* The numbered legend: each MRS line's PLANNED share. It says what the lines asked for, not
                which bin serves which line — that is chosen at pick time. */}
            <div className="mx-5 mb-2.5 flex-shrink-0 rounded-[9px] border border-border bg-muted/35">
              {plan.lines.map((line, index) => {
                const { tag, full } = lineTag(line.item);
                return (
                  <div
                    key={line.key}
                    className={`flex items-start gap-[9px] px-[11px] py-2 ${
                      index === 0 ? '' : 'border-t border-border'
                    }`}
                  >
                    {/* `title` because the tag clips at 26 characters — anything we knowingly clip offers
                        the whole string back on hover (UI-GUIDELINES §8). Native, not a Radix root: the
                        legend repeats per MRS line. */}
                    <span
                      className="inline-flex h-5 shrink-0 items-center gap-[5px] rounded-full border border-border bg-card px-2 text-[11px] font-semibold"
                      title={full}
                    >
                      <span className="text-muted-foreground">{index + 1}</span>
                      {tag}
                    </span>
                    <div className="min-w-0 flex-1 text-[11.5px] leading-[1.45]">
                      <span className="font-mono">{line.item.mrsNumber}</span>
                      {' · '}
                      {plannedLabel(line.pick, line.item.requestedQuantity, item.unitOfMeasure)}
                      {' · '}
                      {line.item.remarks || line.item.netsuiteEndUseName || '—'}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}

        <div className="min-h-0 flex-1 overflow-y-auto px-5">
          {!isLoadingRows && !binsError && binHolds.error && (
            // As for serials: the check failed, not the stock read. The rows show NetSuite's raw figures, and the
            // server still refuses a claim on held units when the picklist is saved.
            <Alert className="mb-2.5 border-warning/40 bg-warning/[0.07]">
              <AlertTriangle className="h-4 w-4 text-warning" />
              <AlertDescription className="text-xs">
                <p>
                  {binHolds.error} The quantities below do not take other picklists into account; taking held units
                  will be refused when you save the picklist.
                </p>
                <Button variant="outline" size="sm" className="mt-2 h-6 text-xs" onClick={binHolds.retry}>
                  <RotateCcw className="mr-1 h-3 w-3" />
                  Retry
                </Button>
              </AlertDescription>
            </Alert>
          )}
          {!isLoadingRows && !binsError && commitments.error && (
            // The check failed, not the stock read: the rows still show, unlabelled, and the server still refuses
            // a held serial. Said plainly, with a way to try again.
            <Alert className="mb-2.5 border-warning/40 bg-warning/[0.07]">
              <AlertTriangle className="h-4 w-4 text-warning" />
              <AlertDescription className="text-xs">
                <p>
                  {commitments.error} Serials on other picklists are not marked below; one of those will be refused
                  when you save the picklist.
                </p>
                <Button variant="outline" size="sm" className="mt-2 h-6 text-xs" onClick={commitments.retry}>
                  <RotateCcw className="mr-1 h-3 w-3" />
                  Retry
                </Button>
              </AlertDescription>
            </Alert>
          )}
          {!isLoadingRows && !binsError && lostChoices > 0 && (
            <p className="mb-2.5 flex items-start gap-1.5 text-[11.5px] leading-[1.45] text-warning">
              <AlertTriangle className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden />
              <span>
                {holdSplit
                  ? lostChoices === 1
                    ? 'A bin you chose is now held by another picklist. Choose another one.'
                    : `${lostChoices} bins you chose are now held by other picklists. Choose others.`
                  : lostChoices === 1
                    ? 'A serial you chose is now on another picklist. Choose another one.'
                    : `${lostChoices} serials you chose are now on other picklists. Choose others.`}
              </span>
            </p>
          )}
          {!isLoadingRows && !binsError && (holdSplit ? holdSplit.heldUnits > 0 : committedBins.length > 0) && (
            // Serials count rows; a lot or untracked item counts units in the same shape (owner, 2026-10-15). Its
            // switch shows only when a whole bin is held — a partly held bin is already on screen at what is free.
            <div className="mb-2 flex flex-wrap items-center justify-between gap-x-3 gap-y-1.5">
              <span className="text-[11.5px] tabular-nums text-muted-foreground">
                {holdSplit
                  ? binHoldCaption(holdSplit.freeUnits, holdSplit.heldUnits, item.unitOfMeasure)
                  : commitmentCaption(binsWithStock.length, committedBins.length)}
              </span>
              {committedBins.length > 0 && (
                <label className="inline-flex cursor-pointer items-center gap-2 text-[11.5px] font-medium">
                  <Switch checked={showAll} onCheckedChange={setShowAll} />
                  {holdSplit ? 'Show held bins' : 'Show all serials'}
                </label>
              )}
            </div>
          )}
          {isLoadingRows ? (
            // Scoped to where the rows will appear. An empty table here would read as "no stock".
            <div className="flex flex-col gap-2 pb-1 pt-1.5">
              {[0, 1, 2, 3].map((k) => (
                <Skeleton key={k} className="h-[34px] rounded-lg" />
              ))}
            </div>
          ) : binsError ? (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription className="text-xs">
                <p>{binsError}</p>
                {onRetryBins && (
                  <Button variant="outline" size="sm" className="mt-2 h-6 text-xs" onClick={onRetryBins}>
                    <RotateCcw className="mr-1 h-3 w-3" />
                    Retry
                  </Button>
                )}
              </AlertDescription>
            </Alert>
          ) : visibleBins.length === 0 ? (
            <p className="my-[18px] text-center text-[12.5px] text-muted-foreground">
              {committedBins.length > 0
                ? holdSplit
                  ? 'Every bin with stock is held by other picklists. Turn on “Show held bins” to see which.'
                  : 'Every serial on hand is on another picklist. Turn on “Show all serials” to see which.'
                : 'No bins with available stock found.'}
            </p>
          ) : (
            <div className="rounded-[9px] border border-border">
              {/* Opaque + sticky: rows must not show through as they scroll under it. The scroller is
                  the wrapper above; giving it `overflow:hidden` would stick this to a box that never
                  scrolls.
                  ⚠️ The opacity lives in TWO classes on purpose. `bg-card` is the background-COLOR;
                  the gradient is the background-IMAGE tint over it. Writing them as one
                  `bg-[linear-gradient(...),hsl(var(--card))]` looks equivalent and is not: Tailwind
                  emits that as a single `background-image`, a bare colour is not a valid image layer,
                  and the browser drops the WHOLE declaration — leaving the header fully transparent
                  with rows scrolling visibly through it. */}
              <div
                className="sticky top-0 z-[5] grid h-9 items-center gap-2.5 rounded-t-lg border-b border-border bg-card bg-[linear-gradient(hsl(var(--muted)/0.4),hsl(var(--muted)/0.4))] px-3 text-[10.5px] font-semibold uppercase tracking-[0.04em] text-muted-foreground shadow-[0_2px_4px_-1px_hsl(var(--foreground)/0.08)]"
                style={{ gridTemplateColumns: gridCols }}
              >
                <span>Bin</span>
                <span>Lot / Serial</span>
                <span className="text-right">Avail</span>
                <span className="text-right">Allocate</span>
              </div>

              {visibleBins.map((bin) => {
                const key = binKeyFromStock(bin);
                const allocated = allocations[key] ?? 0;
                const isOverAllocated = allocated > bin.quantityAvailable;
                const held = commitmentOf(bin, heldBySerial);

                if (heldBinKeys.has(key)) {
                  // A lot or untracked bin other picklists hold entirely (14ykpqtv7nd): shown, never chosen.
                  const heldQty = binHolds.byBin.get(bin.binId) ?? bin.quantityAvailable;
                  return (
                    <HeldRow
                      key={key}
                      rowKey={key}
                      committed="held"
                      bin={bin}
                      gridCols={gridCols}
                      text="On other picklists"
                      tone="bg-warning/10 text-warning"
                      title={`${bin.quantityAvailable} on hand · ${heldQty} held by other open picklists · 0 free`}
                    />
                  );
                }

                if (held) {
                  const label = commitmentLabel(held);
                  return (
                    <HeldRow
                      key={key}
                      rowKey={key}
                      committed={held.status}
                      bin={bin}
                      gridCols={gridCols}
                      text={label.text}
                      tone={label.tone}
                      title={`${label.text} — ${label.title}`}
                    />
                  );
                }

                return (
                  <div
                    key={key}
                    // The row's identity, exposed so a saved allocation can be asserted end to end. Bin
                    // codes repeat across every serial of one bin, so nothing else on the row is unique
                    // enough to anchor to.
                    data-row-key={key}
                    className="grid items-center gap-2.5 border-b border-border px-3 py-[7px]"
                    style={{ gridTemplateColumns: gridCols }}
                  >
                    <span className="font-mono text-xs font-medium">{bin.binCode}</span>
                    <span className="font-mono text-[11.5px] text-muted-foreground">
                      {bin.inventoryNumber ?? '—'}
                    </span>
                    <span className="text-right text-xs tabular-nums">{bin.quantityAvailable}</span>
                    <span className="flex justify-end">
                      <Input
                        type="number"
                        value={allocated || ''}
                        onChange={(e) => handleAllocationChange(key, Number(e.target.value))}
                        className={`h-7 w-[84px] rounded-md px-2 text-right text-xs font-medium ${
                          isOverAllocated ? 'border-destructive text-destructive' : ''
                        }`}
                        min={0}
                        max={bin.quantityAvailable}
                        placeholder="0"
                        // The serial disambiguates: one bin contributes a row PER SERIAL, so the bin
                        // code alone names ten identical controls to a screen reader.
                        aria-label={`Quantity from bin ${bin.binCode}${bin.inventoryNumber ? `, ${bin.inventoryNumber}` : ''}`}
                      />
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Summary bar */}
        <div className="mt-3 flex items-center justify-between gap-2.5 border-t border-border px-5 py-[11px]">
          <div className="min-w-0">
            <span className="text-[12.5px]">
              Allocated:{' '}
              <b className={`font-semibold tabular-nums ${isValid ? 'text-success' : 'text-destructive'}`}>
                {totalAllocated}
              </b>{' '}
              / {pickQuantity} {item.unitOfMeasure}
            </span>
            {multiLine && (
              // The PLANNED split, numbered as the legend is. "Line {n}" is the legend's index, never an
              // MRS line ordinal (D4).
              <div className="mt-[3px] text-[11.5px] leading-[1.45] text-muted-foreground">
                {plan.lines
                  .map((line, index) => `Line ${index + 1}: ${line.pick} ${item.unitOfMeasure}`)
                  .join(' · ')}
              </div>
            )}
          </div>
          {remaining !== 0 && (
            <span
              className={`inline-flex h-5 shrink-0 items-center rounded-full border px-2 text-[11px] font-semibold ${
                remaining > 0 ? 'border-border text-muted-foreground' : 'border-destructive/50 text-destructive'
              }`}
            >
              {remaining > 0 ? `${remaining} remaining` : `${Math.abs(remaining)} over`}
            </span>
          )}
          {overRows > 0 && (
            <span className="inline-flex h-5 shrink-0 items-center rounded-full border border-destructive/50 px-2 text-[11px] font-semibold text-destructive">
              {overRows === 1 ? '1 row is over its bin' : `${overRows} rows are over their bins`}
            </span>
          )}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2.5 border-t border-border px-5 pb-4 pt-3">
          <div className="flex gap-2">
            <Button
              variant="outline"
              className="h-8 rounded-[7px] px-3 text-[12.5px] font-medium"
              onClick={handleAutoAllocate}
              disabled={binsWithStock.length === 0}
            >
              Auto Allocate
            </Button>
            {currentOverride && (
              <Button
                variant="ghost"
                className="h-8 rounded-[7px] px-2.5 text-[12.5px] font-medium text-muted-foreground"
                onClick={handleResetToAuto}
              >
                Reset to Auto
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button
              variant="ghost"
              className="h-8 rounded-[7px] px-3 text-[12.5px] font-medium text-muted-foreground"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              className="h-8 rounded-[7px] px-[15px] text-[12.5px] font-semibold"
              onClick={handleSave}
              disabled={!isValid}
            >
              Save
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
